import React, { useEffect } from 'react';
import { useTheme } from './ThemeProvider';

/**
 * HeroUI Theme Bridge Component
 * Automatically applies appropriate HeroUI theme classes based on current custom theme
 */
export const HeroUIThemeProvider = ({ children }) => {
  const { currentTheme, currentMode } = useTheme();

  useEffect(() => {
    const root = document.documentElement;

    // Map custom themes to HeroUI themes (don't remove mode classes, they're managed by ThemeProvider)
    const heroUIThemeMap = {
      'ocean-blue': `ocean-blue-${currentMode}`,
      'forest-green': `forest-green-${currentMode}`,
      'midnight-purple': `midnight-purple-${currentMode}`,
      'sunset-orange': `sunset-orange-${currentMode}`
    };

    // Only apply custom theme classes if they exist
    const heroUITheme = heroUIThemeMap[currentTheme];
    if (heroUITheme && currentTheme !== 'ocean-blue') { // ocean-blue is default, skip
      root.classList.add(heroUITheme);
    }

    // Ensure data-theme attribute is in sync (read-only, set by ThemeProvider)
    if (!root.getAttribute('data-theme')) {
      root.setAttribute('data-theme', currentMode);
    }
  }, [currentTheme, currentMode]);

  return <>{children}</>;
};

/**
 * Hook to get theme-aware HeroUI classes
 * Use this to get dynamic class names for HeroUI components
 */
export const useHeroUITheme = () => {
  const { currentTheme, currentMode, getCurrentColors } = useTheme();
  
  const colors = getCurrentColors();
  
  // Dynamic classes that respect current theme
  const getThemeAwareClasses = (variant = 'default') => {
    const baseClasses = {
      // Background classes
      background: {
        transparent: 'bg-transparent',
        solid: `bg-theme-background`,
        surface: `bg-theme-surface hover:bg-theme-surface-hover`,
        secondary: `bg-theme-background-secondary`,
        glass: `bg-theme-background/60 backdrop-blur-md backdrop-saturate-150`,
        glassSubtle: `bg-theme-surface/40 backdrop-blur-sm`,
      },
      
      // Border classes  
      border: {
        default: `border-theme-border hover:border-theme-border-hover`,
        subtle: `border-theme-border/20`,
        strong: `border-theme-border`,
      },
      
      // Text classes
      text: {
        primary: `text-theme-text`,
        secondary: `text-theme-text-secondary`,
        onPrimary: 'text-white',
        onSurface: `text-theme-text`,
      }
    };
    
    return baseClasses;
  };
  
  // Pre-built component class combinations
  const componentClasses = {
    navbar: {
      base: 'bg-theme-surface/80 backdrop-blur-md border-theme-border/20',
      glass: 'bg-theme-background/60 backdrop-blur-md backdrop-saturate-150 border-theme-border/20',
      transparent: 'bg-transparent backdrop-filter-none',
    },
    
    navbarMenu: {
      base: 'bg-theme-surface/90 backdrop-blur-md backdrop-saturate-150 border-theme-border/10',
      glass: 'bg-theme-background/50 backdrop-blur-md backdrop-saturate-150',
    },
    
    button: {
      primary: `bg-theme-primary hover:bg-theme-primary-hover text-white`,
      secondary: `bg-theme-secondary hover:bg-theme-surface-hover text-theme-text`,
      surface: `bg-theme-surface hover:bg-theme-surface-hover text-theme-text border-theme-border`,
      ghost: `bg-transparent hover:bg-theme-surface-hover text-theme-text`,
    },
    
    card: {
      base: `bg-theme-surface border-theme-border hover:border-theme-border-hover`,
      elevated: `bg-theme-surface shadow-lg border-theme-border/50`,
    }
  };
  
  return {
    currentTheme,
    currentMode,
    colors,
    getThemeAwareClasses,
    componentClasses,
  };
};